import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  ButtonProps,
} from '@mui/material';
import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { BasicTabs } from '@/components/shared/BasicTabs';
import {
  allPointOfInterestTemplates as allPointOfInterestTemplatesState,
  selectedTemplateFiltersState,
  poiCountsByType,
  selectedFilteredDeckIds,
  selectedFilteredPaintRegionIds,
  selectedFilteredWorkpackIds,
  applyFilters,
  customFilterQueryVarsState,
} from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { PointOfInterestTable } from './PointOfInterestTable';
import { AddToReport } from '@/components/shared/ReportsRevamped/AddToReport';
import { PageLoader } from '@/components/shared/PageLoader';
import { contentStyles, paperStyles, pointOfInterestTabsStyles, titleStyles } from './styles';
import { SelectedValues } from '@/components/shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { TemplateTypeWithAllTab } from '@/types';
import SearchIcon from '@mui/icons-material/Search';
import { ALL_POIS_TAB_LABEL, LocationTag } from '@/constants';

import AddIcon from '@mui/icons-material/Add';
import { Workpacks } from './Workpacks';
import { abyssColors } from '@/theme/colors';
import { useDebounce } from '@/hooks/useDebounce';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { editPointOfInterest as editPointOfInterestState } from '@/components/Analysis/modules/pointOfInterest/state';
import { usePoiFlow } from './usePoiFlow';
import { useFeatureFlag } from '@/hooks';
import {
  PoiBulkUploadActionTypes,
  useBulkUploadPoiActions,
} from '@/components/Analysis/modules/pointOfInterest/BulkUpload/actions';
import { ColumnsSelectionDialog } from '../../ColumnsSelectionDialog';
import { ExportCsvSubheading } from '../ExportCsvSubheading';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { usePoiColumns } from '@/hooks/usePoiColumns';
import {
  PointOfInterestsCountByTypeQuery,
  PointOfInterestTemplateTypeEnum,
  ReportInternalType,
  usePointOfInterestsCountByTypeQuery,
} from '@/__generated__/graphql';
import { useXlsxDownloader } from './useXlsxDownloader';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { Workflow } from './Workflow';
import { useMappedFilters } from '@/hooks/useMappedFilters';
import { useLiveSyncAllPois } from '@/components/Analysis/shared/hooks/useLiveSyncAllPois';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { GenericDialog } from '../../../../shared/GenericDialog';
import { toKebabCase, upperSnakeCase } from '@/utils';
import { usePoiFilterDeepLinking } from '@/hooks/usePoiFilterDeepLinking';
import { useCustomFilters } from '@/hooks/useCustomFilters';
import { useViewByUrlSync } from '../../deeplink';
import { Flight, SystemUpdateAlt } from '@mui/icons-material';
import { SmartPoiImportModal } from '@/components/Analysis/modules/pointOfInterest/BulkUpload/components/SmartPoiImportModal';
import { Pill } from '@/components/shared/Pill';

const VIEW_BY_OPTIONS = ['poi', 'workpack', 'workflow'] as const;
type ViewBy = (typeof VIEW_BY_OPTIONS)[number];
export const PointOfInterestsContainer = () => {
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const [selectedPOIType, setSelectedPOIType] = useState<number>(0);
  const allPointOfInterestTemplates = useAtomValue(allPointOfInterestTemplatesState);
  const editPointOfInterest = useAtomValue(editPointOfInterestState);

  const selectedTemplateFilters = useAtomValue(selectedTemplateFiltersState);
  const [appliedTemplateFilters, setAppliedTemplateFilters] = useState(selectedTemplateFilters);

  const appliedCustomFilterQueryVars = useAtomValue(customFilterQueryVarsState);

  const structureId = useGetStructureId();
  const applyQueryFilters = useAtom(applyFilters);
  const selectedDeckIds = useAtomValue(selectedFilteredDeckIds);
  const [appliedDeckFilters, setAppliedDeckFilters] = useState(selectedDeckIds);

  const selectedPaintRegionIds = useAtomValue(selectedFilteredPaintRegionIds);
  const selectedWorkpackIds = useAtomValue(selectedFilteredWorkpackIds);
  const [appliedWorkpackFilters, setAppliedWorkpackFilters] = useState(selectedWorkpackIds);
  const [appliedPaintRegionFilters, setAppliedPaintRegionFilters] =
    useState(selectedPaintRegionIds);

  const [selectedFilterValues, setSelectedFilterValues] = useState<SelectedValues>({});
  const [workpackIds, setWorkpackIds] = useState<string[]>([]);
  const [viewBy, setViewBy] = useState<ViewBy>('poi');
  useViewByUrlSync(viewBy, setViewBy, VIEW_BY_OPTIONS);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 700);
  const previousPOITypeRef = useRef<number>(0);

  const [isSmartPoiImportModalOpen, setIsSmartPoiImportModalOpen] = useState(false);

  // Custom filters (height from deck, workpack filter)
  const {
    customFiltersConfig,
    customFilterValues,
    handleCustomFilterChange,
    customFilterQueryVars,
    resetCustomFilters,
  } = useCustomFilters();
  const { handleCreatePoi } = usePoiFlow();
  const setPoiCounts = useSetAtom(poiCountsByType);
  const poiCounts = useAtomValue(poiCountsByType);

  const isAdmin = useFeatureFlag('admin');
  const isPilotUser = useFeatureFlag('pilot-tester');
  const { dispatch: poiBulkUploadDispatch } = useBulkUploadPoiActions();
  const { getUnitSuffix } = useUnitConversion();

  const [selectedPointOfInterests, setSelectedPointOfInterests] = useState<string[]>([]);
  const [isAddToReportOpen, setIsAddToReportOpen] = useState(false);
  const [exportXlsxDialogOpen, setExportXlsxDialogOpen] = useState<boolean>(false);
  const allPOIsTemplatesWithAllTab: TemplateTypeWithAllTab[] = useMemo(
    () => [
      {
        name: ALL_POIS_TAB_LABEL,
        type: '',
        fields: allPointOfInterestTemplates?.[0]?.fields ?? [],
        key: 'all',
      },
      ...(allPointOfInterestTemplates ?? []),
    ],
    [allPointOfInterestTemplates]
  );
  const selectedPoiTemplate = allPOIsTemplatesWithAllTab[selectedPOIType];

  // Check if any template in the deployment has VolumeOfInterest type
  const hasVolumeOfInterest = useMemo(() => {
    return (allPointOfInterestTemplates ?? []).some(
      (template) =>
        template.type === PointOfInterestTemplateTypeEnum.VolumeOfInterest ||
        template.type === PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest
    );
  }, [allPointOfInterestTemplates]);

  const changePOITypeTab = useCallback((updatedPOIType: number) => {
    setSelectedPOIType(updatedPOIType);
  }, []);

  const handlePointOfInterestSelect = useCallback((selectedPointOfInterestIds: string[]) => {
    setSelectedPointOfInterests(selectedPointOfInterestIds);
  }, []);

  const toggleAddToReportOpen = useCallback(
    (status: boolean) => () => {
      setIsAddToReportOpen(status);
    },
    []
  );

  useEffect(() => {
    // Only clear filters if POI type changed from a non-default value
    // This allows URL restoration (0 → X) without clearing, but clears on user changes (X → Y)
    const previousType = previousPOITypeRef.current;
    previousPOITypeRef.current = selectedPOIType;

    // Skip clearing if changing from initial default (0) - this is URL restoration
    if (previousType === 0) {
      return;
    }

    // Clear filters when user manually changes POI type
    setSelectedFilterValues(() => ({}));
    setSelectedPointOfInterests([]);
  }, [selectedPOIType]);

  const clearSelectedPOIs = useCallback(() => {
    setSelectedPointOfInterests([]);
  }, []);

  const selectedPointOfInterestIds = useMemo(() => {
    return [...new Set(Object.values(selectedPointOfInterests).flat())];
  }, [selectedPointOfInterests]);

  const type =
    allPOIsTemplatesWithAllTab[selectedPOIType]?.name === ALL_POIS_TAB_LABEL
      ? undefined
      : allPOIsTemplatesWithAllTab[selectedPOIType]?.name.replace(/ /g, '_').toUpperCase();
  const { userColumns, refetchUserPreferences } = useUserPreferences(
    structureId,
    type ?? 'ALL',
    'xlsx'
  );

  const {
    selectedPoiFilters,
    isGoverningFilters,
    isVerifiedFilters,
    accessibilityFilters,
    statusFilters,
    isManuallyCreatedFilters,
    unAssignedPoiFilters,
    mediaAttachmentFilters,
    deckFilters,
  } = useMappedFilters(selectedFilterValues);

  usePoiFilterDeepLinking({
    selectedFilterValues,
    setSelectedFilterValues,
    customFilterValues,
    handleCustomFilterChange,
    workpackIds,
    setWorkpackIds,
    searchTerm,
    setSearchTerm,
    selectedPOIType,
    setSelectedPOIType,
    allPOIsTemplatesWithAllTab,
  });

  const handleResetFilters = useCallback(() => {
    setSelectedFilterValues({});
    setWorkpackIds([]);
    setSearchTerm('');
    resetCustomFilters();
  }, [setSelectedFilterValues, setWorkpackIds, setSearchTerm, resetCustomFilters]);

  // TODO: refactor this to avoid reuse in summary component
  const queryInput = useMemo(
    () => ({
      selectedTemplateFilters: appliedTemplateFilters,
      assemblyTemplateId: selectedAssemblyTemplateId,
      selectedPoiFilters,
      structureId,
      paintRegionIds: appliedPaintRegionFilters ?? [],
      workpackIds: [...new Set([...(workpackIds ?? []), ...(appliedWorkpackFilters ?? [])])],
      isVerified: isVerifiedFilters,
      isManuallyCreated: isManuallyCreatedFilters,
      isGoverning: isGoverningFilters,
      deckIds: deckFilters,
      unAssignedPoi: unAssignedPoiFilters,
      accessibility: accessibilityFilters,
      status: statusFilters,
      searchTerm: debouncedSearchTerm,
      mediaAttachment: mediaAttachmentFilters,
      includeManuallyCreated: true,
      selectedDeckIds: appliedDeckFilters,
      ...appliedCustomFilterQueryVars,
    }),
    [
      selectedAssemblyTemplateId,
      appliedTemplateFilters,
      selectedPoiFilters,
      structureId,
      selectedFilterValues,
      appliedPaintRegionFilters,
      isVerifiedFilters,
      isManuallyCreatedFilters,
      unAssignedPoiFilters,
      accessibilityFilters,
      statusFilters,
      debouncedSearchTerm,
      mediaAttachmentFilters,
      appliedDeckFilters,
      appliedWorkpackFilters,
      workpackIds,
      isGoverningFilters,
      appliedCustomFilterQueryVars,
      deckFilters,
    ]
  );

  const onCountsCompleted = (data: PointOfInterestsCountByTypeQuery) => {
    if (data?.pointOfInterestsCountByType) {
      setPoiCounts(
        data.pointOfInterestsCountByType
          .filter(
            (item): item is { type: string; count: number } =>
              item?.type !== null && item?.count !== null
          )
          .map(({ type, count }) => ({ type, count }))
      );
    }
  };

  const { loading: loadingCounts, refetch: refetchCounts } = usePointOfInterestsCountByTypeQuery({
    skip: !structureId || !selectedAssemblyTemplateId,
    variables: {
      input: queryInput,
    },
    onCompleted: onCountsCompleted,
  });

  useLiveSyncAllPois(() => {
    refetchCounts().then((result) => {
      if (result?.data) {
        onCountsCompleted(result.data);
      }
    });
  });

  useEffect(() => {
    if (applyQueryFilters) {
      setAppliedTemplateFilters(selectedTemplateFilters);
      setAppliedDeckFilters(selectedDeckIds);
      setAppliedPaintRegionFilters(selectedPaintRegionIds);
      setAppliedWorkpackFilters(selectedWorkpackIds);
    }
  }, [
    applyQueryFilters,
    selectedDeckIds,
    selectedPaintRegionIds,
    selectedTemplateFilters,
    selectedWorkpackIds,
  ]);

  const columns = usePoiColumns({
    selectedPoiTemplate,
    type,
    include3DView: true,
    hasVolumeOfInterest,
  });
  const mainContainerTabs = useMemo(() => {
    return allPOIsTemplatesWithAllTab.map((template, index) => {
      const typeCount =
        template.name === ALL_POIS_TAB_LABEL
          ? poiCounts.find((poiCount) => poiCount?.type === 'All')?.count || 0
          : poiCounts.find((poiCount) => {
              const type = poiCount?.type?.toLowerCase();
              const name = template?.key?.toLowerCase() ?? template?.name?.toLowerCase();
              const upperSnakeCaseName = upperSnakeCase(template.name).toLowerCase();
              return upperSnakeCaseName === type || name === type;
            })?.count || 0;

      const notVolumeOfInterest =
        viewBy === 'workflow' &&
        template?.type !== PointOfInterestTemplateTypeEnum.VolumeOfInterest &&
        template?.type !== PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest;
      return {
        index,
        id: toKebabCase(template.name),
        label: `${template.name} ${loadingCounts ? '-' : typeCount}`,
        testid: `poi-tab-${template.name}`,
        disabled: notVolumeOfInterest,
        component: (
          <>
            {viewBy === 'poi' && (
              <PointOfInterestTable
                template={template}
                selectedPointOfInterests={selectedPointOfInterests}
                handlePointOfInterestSelect={handlePointOfInterestSelect}
                clearSelectedPOIs={clearSelectedPOIs}
                selectedTab={template.name}
                setSelectedFilterValues={setSelectedFilterValues}
                selectedFilterValues={selectedFilterValues}
                setWorkpackIds={setWorkpackIds}
                workpackIds={workpackIds}
                isVerifiedFilters={isVerifiedFilters}
                isManuallyCreatedFilters={isManuallyCreatedFilters}
                isGoverningFilters={isGoverningFilters}
                unAssignedPoiFilters={unAssignedPoiFilters}
                mediaAttachmentFilters={mediaAttachmentFilters}
                accessibilityFilters={accessibilityFilters}
                statusFilters={statusFilters}
                selectedPoiFilters={selectedPoiFilters}
                columns={columns}
                workpackId={undefined}
                debouncedSearchTerm={debouncedSearchTerm}
                viewBy={viewBy}
                deckFilters={deckFilters}
                customFiltersConfig={customFiltersConfig}
                customFilterValues={customFilterValues}
                handleCustomFilterChange={handleCustomFilterChange}
                customFilterQueryVars={customFilterQueryVars}
              />
            )}
          </>
        ),
      };
    });
  }, [
    allPOIsTemplatesWithAllTab,
    poiCounts,
    loadingCounts,
    viewBy,
    selectedPointOfInterests,
    handlePointOfInterestSelect,
    clearSelectedPOIs,
    selectedFilterValues,
    isVerifiedFilters,
    isManuallyCreatedFilters,
    isGoverningFilters,
    unAssignedPoiFilters,
    mediaAttachmentFilters,
    accessibilityFilters,
    statusFilters,
    selectedPoiFilters,
    columns,
    debouncedSearchTerm,
    workpackIds,
    deckFilters,
    customFiltersConfig,
    customFilterValues,
    handleCustomFilterChange,
    customFilterQueryVars,
  ]);

  const handleViewByOption = useCallback((event: SelectChangeEvent<string>) => {
    setViewBy(event.target.value as ViewBy);
    setSelectedPointOfInterests([]);
    setSearchTerm('');
    setSelectedFilterValues({});
  }, []);

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm]
  );

  const { downloadXLSX } = useXlsxDownloader({
    type: type ?? '',
    selectedPoiFilters,
    paintRegionIds: [],
    isVerified: isVerifiedFilters,
    isGoverning: isGoverningFilters,
    accessibility: accessibilityFilters,
    status: statusFilters,
    workpackIds: selectedWorkpackIds,
  });

  const poisActionButtons = useMemo(() => {
    const isDisabled = editPointOfInterest.state === 'WaitingForDetail';
    return [
      {
        label: 'Export Excel',
        icon: <AbyssIcon iconName="excel" size="md" />,
        isHidden: false,
        isDisabled,
        action: () => {
          refetchUserPreferences();
          setExportXlsxDialogOpen(true);
        },
      },
      {
        label: 'Add to Report',
        icon: <AbyssIcon iconName="file-copy" size="md" />,
        isHidden: false,
        isDisabled,
        action: toggleAddToReportOpen(true),
      },
      {
        label: 'Smart POI Import',
        icon: <SystemUpdateAlt sx={{ fontSize: '1.6rem !important' }} />,
        isHidden: !isPilotUser,
        isDisabled: false,
        action: () => setIsSmartPoiImportModalOpen(true),
        endIcon: (
          <Pill
            label="Pilot"
            status="linked"
            size="small"
            state="hover"
            disabled
            showIcon
            icon={<Flight />}
          />
        ),
      },
      {
        label: 'Import POI',
        icon: <AbyssIcon iconName="import" size="md" />,
        isHidden: !isAdmin,
        isDisabled: false,
        action: () =>
          poiBulkUploadDispatch({
            type: PoiBulkUploadActionTypes.OPEN_UPLOAD_MODAL,
          }),
      },

      {
        label: 'Create POI',
        icon: <AddIcon sx={{ fontSize: '1.6rem !important' }} />,
        isHidden: false,
        isDisabled,
        variant: 'contained' as ButtonProps['variant'],
        action: handleCreatePoi,
      },
    ].filter(({ isHidden }) => !isHidden);
  }, [
    editPointOfInterest.state,
    toggleAddToReportOpen,
    isAdmin,
    poiBulkUploadDispatch,
    handleCreatePoi,
    refetchUserPreferences,
  ]);

  const dialogColumns = useMemo(() => {
    const updatedColumns = [...userColumns];

    if (!updatedColumns.includes('assembly')) {
      updatedColumns.push('assembly');
    }

    const columnOptions = columns
      .map(({ key, columnName }) => ({
        key,
        templateFieldKey: key,
        name: columnName ?? key.toUpperCase(),
        isSelected: updatedColumns.includes(key),
      }))
      .filter(({ key }) => key !== 'view3D');

    // Add Platform Name column option
    columnOptions.push({
      key: 'platformName',
      templateFieldKey: 'platformName',
      name: 'Platform Name',
      isSelected: updatedColumns.includes('platformName'),
    });

    return columnOptions;
  }, [columns, userColumns]);

  const handleExportXlsx = useCallback(
    async (selectedColumnsKeys: string[]) => {
      const includePlatformName = selectedColumnsKeys.includes('platformName');
      const selectedColumns = columns
        .filter(({ key }) => selectedColumnsKeys.includes(key))
        .map((column) => {
          const { columnName, key } = column;
          let formattedColumnName = columnName ?? key.toUpperCase();
          const unitSuffix = getUnitSuffix({ field: key });
          formattedColumnName = unitSuffix
            ? `${formattedColumnName} (${unitSuffix})`
            : formattedColumnName;
          return { ...column, columnName: formattedColumnName };
        });
      await downloadXLSX(selectedColumns, includePlatformName);
    },
    [columns, downloadXLSX, getUnitSuffix]
  );

  return (
    <Box pb={4}>
      <Box
        sx={{
          position: 'relative',
          top: '58px',
          display: 'none',
        }}
      >
        <PageLoader />
      </Box>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      >
        <Stack direction="row" my={2} alignItems="center">
          <Stack flex={1} direction="row" alignItems="center" gap="1.2rem">
            <Typography
              noWrap
              variant="caption"
              sx={{
                color: abyssColors.primary[300],
              }}
            >
              View by:
            </Typography>
            <Select
              value={viewBy}
              onChange={handleViewByOption}
              data-testid="view-by-select"
              sx={{
                typography: 'caption',
                '& .MuiInputBase-input': { padding: '0.5rem 0.8rem' },
                '& .MuiSvgIcon-root': { fontSize: '1.6rem' },
              }}
            >
              <MenuItem value="poi" sx={{ typography: 'caption' }}>
                Single POI
              </MenuItem>
              <MenuItem value="workpack" sx={{ typography: 'caption' }}>
                Workpack
              </MenuItem>
              <MenuItem value="workflow" sx={{ typography: 'caption' }}>
                Saved blocks
              </MenuItem>
            </Select>
            <TextField
              size="small"
              sx={{
                minWidth: '40rem',
                padding: 0,
                '& .MuiInputBase-input': { padding: '0.5rem 0.8rem' },
              }}
              placeholder="Search"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                sx: {
                  height: 'fit-content',
                  fontSize: '1.2rem',
                  lineHeight: 'normal',
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ fontSize: '1.6rem' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1} height="fit-content" alignItems="center">
            {poisActionButtons.map(({ isDisabled, label, icon, variant, action, endIcon }) => {
              return (
                <Button
                  key={label}
                  startIcon={icon}
                  variant={variant ?? 'outlined'}
                  size="small"
                  onClick={action}
                  disabled={isDisabled}
                  endIcon={endIcon}
                >
                  {label}
                </Button>
              );
            })}
          </Stack>
        </Stack>
        <BasicTabs
          tabs={mainContainerTabs}
          selectedTab={selectedPOIType}
          changeTab={changePOITypeTab}
          fullWidth={false}
          variant="pills"
          tabsStyles={pointOfInterestTabsStyles}
          handleResetFilters={handleResetFilters}
          tabIdPrefix={LocationTag.InsightsPoiTableType}
        />
        {viewBy === 'workpack' && (
          <Workpacks
            template={selectedPoiTemplate}
            selectedPointOfInterests={selectedPointOfInterests}
            handlePointOfInterestSelect={handlePointOfInterestSelect}
            clearSelectedPOIs={clearSelectedPOIs}
            selectedTab={allPOIsTemplatesWithAllTab[selectedPOIType].name}
            setSelectedFilterValues={setSelectedFilterValues}
            selectedFilterValues={selectedFilterValues}
            workpackIds={workpackIds}
            setWorkpackIds={setWorkpackIds}
            isVerifiedFilters={isVerifiedFilters}
            isGoverningFilters={isGoverningFilters}
            accessibilityFilters={accessibilityFilters}
            statusFilters={statusFilters}
            selectedPoiFilters={selectedPoiFilters}
            columns={columns}
            searchTerm={debouncedSearchTerm}
            handleResetFilters={handleResetFilters}
            deckFilters={deckFilters}
          />
        )}
        {viewBy === 'workflow' && (
          <Workflow
            template={selectedPoiTemplate}
            selectedPointOfInterests={selectedPointOfInterests}
            handlePointOfInterestSelect={handlePointOfInterestSelect}
            clearSelectedPOIs={clearSelectedPOIs}
            selectedTab={allPOIsTemplatesWithAllTab[selectedPOIType].name}
            setSelectedFilterValues={setSelectedFilterValues}
            selectedFilterValues={selectedFilterValues}
            setWorkpackIds={setWorkpackIds}
            isVerifiedFilters={isVerifiedFilters}
            isGoverningFilters={isGoverningFilters}
            accessibilityFilters={accessibilityFilters}
            statusFilters={statusFilters}
            selectedPoiFilters={selectedPoiFilters}
            columns={columns}
            searchTerm={debouncedSearchTerm}
            handleResetFilters={handleResetFilters}
            viewBy={viewBy}
            deckFilters={deckFilters}
          />
        )}
        {isAddToReportOpen && (
          <GenericDialog
            open={isAddToReportOpen}
            onClose={toggleAddToReportOpen(false)}
            title="Add point of interests to report"
            paperStyles={paperStyles}
            contentStyles={contentStyles}
            titleStyles={titleStyles}
            showCloseIcon
            content={
              <AddToReport
                reportCreated={clearSelectedPOIs}
                handleClose={toggleAddToReportOpen(false)}
                selectedPointOfInterestIds={selectedPointOfInterestIds}
                internalType={ReportInternalType.PointOfInterest}
              />
            }
          />
        )}

        <ColumnsSelectionDialog
          open={exportXlsxDialogOpen}
          columns={dialogColumns}
          heading="Customize Table Columns"
          subHeading={<ExportCsvSubheading />}
          applyButtonText="Download"
          applyButtonLoadingText="Downloading..."
          successMessage="The selected columns have been successfully exported as a .xlsx file."
          errorMessage="Failed to export Excel file"
          userPreferenceKey={type ?? 'ALL'}
          userPreferenceType="xlsx"
          onApply={handleExportXlsx}
          onClose={() => setExportXlsxDialogOpen(false)}
        />
        <SmartPoiImportModal
          open={isSmartPoiImportModalOpen}
          onClose={() => setIsSmartPoiImportModalOpen(false)}
          onUploadComplete={(files) => {
            console.log('Uploaded files:', files);
            // Handle uploaded files
          }}
        />
      </Box>
    </Box>
  );
};
